export interface User {
  id: string;
  name: string;
  email: string;
  avatar: string;
  department: string;
  jobTitle: string;
  team: string;
  selected: boolean;
}

export interface LearningItem {
  id: string;
  title: string;
  type: 'Course' | 'Path' | 'Certification';
  category: string;
  duration: string;
  modules: number;
  icon: string;
  selected: boolean;
}

export interface Assignment {
  id: string;
  name: string;
  itemCount: number;
  assignedDate: string;
  assignedBy: string;
  totalUsers: number;
  completionPercentage: number;
  doneCount: number;
  activeCount: number;
  pendingCount: number;
  dueDate: string;
}

export interface UserProgress {
  id: string;
  name: string;
  email: string;
  status: 'Completed' | 'In Progress' | 'Not Started';
  progress: number;
  lastActivity: string;
  avatar: string;
}

export const getUsers = async (): Promise<{
  success: boolean;
  data?: User[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: User[] = [
      { id: '1', name: 'Sarah Johnson', email: 'sarah.j@company.com', avatar: 'SJ', department: 'Engineering', jobTitle: 'Senior Developer', team: 'Backend Team', selected: false },
      { id: '2', name: 'Michael Chen', email: 'michael.c@company.com', avatar: 'MC', department: 'Engineering', jobTitle: 'Frontend Developer', team: 'Frontend Team', selected: false },
      { id: '3', name: 'Emily Rodriguez', email: 'emily.r@company.com', avatar: 'ER', department: 'Engineering', jobTitle: 'DevOps Engineer', team: 'Infrastructure Team', selected: false },
      { id: '4', name: 'David Kim', email: 'david.k@company.com', avatar: 'DK', department: 'Engineering', jobTitle: 'Junior Developer', team: 'Backend Team', selected: false },
      { id: '5', name: 'Jessica Taylor', email: 'jessica.t@company.com', avatar: 'JT', department: 'Marketing', jobTitle: 'Marketing Manager', team: 'Growth Team', selected: false }
    ];

    await new Promise(resolve => setTimeout(resolve, 450));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<User[]>(ENDPOINTS.USERS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch users' }
    };
  }
};

export const getLearningItems = async (): Promise<{
  success: boolean;
  data?: LearningItem[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: LearningItem[] = [
      { id: '1', title: 'Leadership Essentials', type: 'Course', category: 'Leadership', duration: '2 hours', modules: 8, icon: 'book', selected: false },
      { id: '2', title: 'Advanced React Development', type: 'Course', category: 'Technical', duration: '4 hours', modules: 12, icon: 'book', selected: false },
      { id: '3', title: 'Cybersecurity Fundamentals', type: 'Course', category: 'Security', duration: '3 hours', modules: 10, icon: 'book', selected: false },
      { id: '4', title: 'Data Analytics 101', type: 'Course', category: 'Data Science', duration: '2.5 hours', modules: 9, icon: 'book', selected: false },
      { id: '5', title: 'Agile Methodology', type: 'Course', category: 'Project Management', duration: '1.5 hours', modules: 6, icon: 'book', selected: false },
      { id: '6', title: 'Leadership Development Path', type: 'Path', category: 'Leadership', duration: '12 hours', modules: 25, icon: 'target', selected: false },
      { id: '7', title: 'Technical Excellence Track', type: 'Path', category: 'Technical', duration: '20 hours', modules: 40, icon: 'target', selected: false },
      { id: '8', title: 'Professional Leadership Certification', type: 'Certification', category: 'Leadership', duration: '8 hours', modules: 15, icon: 'award', selected: false },
      { id: '9', title: 'Advanced Technical Certification', type: 'Certification', category: 'Technical', duration: '10 hours', modules: 20, icon: 'award', selected: false },
      { id: '10', title: 'Marketing Fundamentals', type: 'Course', category: 'Marketing', duration: '2 hours', modules: 8, icon: 'book', selected: false },
      { id: '11', title: 'Sales Mastery Course', type: 'Course', category: 'Sales', duration: '3 hours', modules: 10, icon: 'book', selected: false }
    ];

    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<LearningItem[]>(ENDPOINTS.LEARNING_ITEMS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch learning items' }
    };
  }
};

export const getAssignments = async (): Promise<{
  success: boolean;
  data?: Assignment[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: Assignment[] = [
      {
        id: '1',
        name: 'Q4 2025 Compliance Training',
        itemCount: 2,
        assignedDate: '2025-10-01',
        assignedBy: 'Admin User',
        totalUsers: 145,
        completionPercentage: 68,
        doneCount: 70,
        activeCount: 50,
        pendingCount: 25,
        dueDate: '2025-12-31'
      },
      {
        id: '2',
        name: 'Engineering Team - Advanced Topics',
        itemCount: 2,
        assignedDate: '2025-11-15',
        assignedBy: 'Sarah Johnson',
        totalUsers: 32,
        completionPercentage: 42,
        doneCount: 6,
        activeCount: 14,
        pendingCount: 12,
        dueDate: '2026-01-31'
      },
      {
        id: '3',
        name: 'Sales Onboarding - New Hires',
        itemCount: 3,
        assignedDate: '2025-11-20',
        assignedBy: 'Robert Anderson',
        totalUsers: 8,
        completionPercentage: 85,
        doneCount: 5,
        activeCount: 2,
        pendingCount: 1,
        dueDate: '2025-12-15'
      },
      {
        id: '4',
        name: 'Leadership Development Program',
        itemCount: 3,
        assignedDate: '2025-09-01',
        assignedBy: 'HR Admin',
        totalUsers: 24,
        completionPercentage: 55,
        doneCount: 6,
        activeCount: 10,
        pendingCount: 8,
        dueDate: '2026-02-28'
      },
      {
        id: '5',
        name: 'Cybersecurity Awareness - All Staff',
        itemCount: 2,
        assignedDate: '2025-08-15',
        assignedBy: 'IT Security',
        totalUsers: 187,
        completionPercentage: 92,
        doneCount: 172,
        activeCount: 12,
        pendingCount: 3,
        dueDate: '2025-11-30'
      }
    ];

    await new Promise(resolve => setTimeout(resolve, 550));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<Assignment[]>(ENDPOINTS.ASSIGNMENTS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch assignments' }
    };
  }
};

export const getUserProgress = async (): Promise<{
  success: boolean;
  data?: UserProgress[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: UserProgress[] = [
      { id: '1', name: 'Sarah Johnson', email: 'sarah.j@company.com', status: 'Completed', progress: 100, lastActivity: '1 week ago', avatar: 'SJ' },
      { id: '2', name: 'Michael Chen', email: 'michael.c@company.com', status: 'Completed', progress: 100, lastActivity: '5 days ago', avatar: 'MC' },
      { id: '3', name: 'Emily Rodriguez', email: 'emily.r@company.com', status: 'Completed', progress: 100, lastActivity: '1 week ago', avatar: 'ER' },
      { id: '4', name: 'David Kim', email: 'david.k@company.com', status: 'Completed', progress: 100, lastActivity: '1 week ago', avatar: 'DK' },
      { id: '5', name: 'Jessica Taylor', email: 'jessica.t@company.com', status: 'Completed', progress: 100, lastActivity: '1 week ago', avatar: 'JT' },
      { id: '6', name: 'Robert Anderson', email: 'robert.a@company.com', status: 'Completed', progress: 100, lastActivity: '5 days ago', avatar: 'RA' },
      { id: '7', name: 'Amanda White', email: 'amanda.w@company.com', status: 'Completed', progress: 100, lastActivity: '2 days ago', avatar: 'AW' },
      { id: '8', name: 'James Wilson', email: 'james.w@company.com', status: 'Completed', progress: 100, lastActivity: '5 days ago', avatar: 'JW' },
      { id: '9', name: 'Lisa Brown', email: 'lisa.b@company.com', status: 'Completed', progress: 100, lastActivity: '2 days ago', avatar: 'LB' },
      { id: '10', name: 'Tom Martinez', email: 'tom.m@company.com', status: 'Completed', progress: 100, lastActivity: '1 week ago', avatar: 'TM' },
      { id: '11', name: 'Rachel Green', email: 'rachel.g@company.com', status: 'Completed', progress: 100, lastActivity: '1 week ago', avatar: 'RG' },
      { id: '12', name: 'Kevin Lee', email: 'kevin.l@company.com', status: 'Completed', progress: 100, lastActivity: '5 days ago', avatar: 'KL' }
    ];

    await new Promise(resolve => setTimeout(resolve, 400));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<UserProgress[]>(ENDPOINTS.USER_PROGRESS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch user progress' }
    };
  }
};
